import {
    Button,
    Checkbox,
    ErrorAlert,
    FormError,
    Notice,
    PasswordInput,
    Spinner,
    TextInput,
    linkStyles,
    useTextForm,
} from '@lumlab/platform-ui';
import { registrationInput, termsVersion } from '@lumlab/platform-identity/contract';
import { apiRequest } from '@lumlab/platform-product/web';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router';
import { authClient } from '../app/auth.ts';
import { AuthScreen } from '../app/AuthScreen.tsx';
import { useRegistrationConfig } from '../app/account.ts';
const RegistrationProtection = lazy(() =>
    import('../app/RegistrationProtection.tsx').then((module) => ({ default: module.RegistrationProtection })),
);

export function SignUpPage() {
    const { t } = useTranslation();
    const session = authClient.useSession();
    const config = useRegistrationConfig();
    const navigate = useNavigate();
    const [proof, setProof] = useState('');
    const [proofKey, setProofKey] = useState(0);
    const [accepted, setAccepted] = useState(false);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const form = useTextForm({
        initialValues: { name: '', email: '', organization: '', password: '', confirmation: '' },
        validate: (values) => ({
            password: values.password.length < 12 ? t('platform.auth.resetPassword.tooShort') : undefined,
            confirmation:
                values.password !== values.confirmation ? t('platform.auth.resetPassword.mismatch') : undefined,
        }),
    });
    const submit = form.onSubmit(async (values) => {
        setPending(true);
        setError(null);
        try {
            const input = registrationInput.parse({
                ...values,
                email: values.email.trim().toLowerCase(),
                termsAccepted: accepted,
                termsVersion,
                proof,
            });
            await apiRequest('/api/registration', { method: 'POST', body: input });
            await navigate('/verify-email', { state: { email: input.email } });
        } catch (failure) {
            setError(failure);
            setProof('');
            setProofKey((key) => key + 1);
        } finally {
            setPending(false);
        }
    });
    if (session.data) return <Navigate to="/" replace />;
    return (
        <AuthScreen title={t('platform.auth.signUp.title')}>
            {config.isPending ? (
                <Spinner label={t('platform.shell.loading')} />
            ) : config.error ? (
                <ErrorAlert error={config.error} />
            ) : !config.data.enabled ? (
                <Notice>
                    {t('platform.auth.signUp.closed')}{' '}
                    <a className={linkStyles} href={config.data.contactUrl}>
                        {t('platform.site.terms.contact')}
                    </a>
                </Notice>
            ) : (
                <form className="flex flex-col gap-4" onSubmit={submit}>
                    <p className="text-sm text-muted">{t('platform.auth.signUp.intro')}</p>
                    <FormError message={form.formError} />
                    {error !== null && <ErrorAlert error={error} />}
                    <TextInput
                        label={t('platform.auth.signUp.name')}
                        autoComplete="name"
                        required
                        maxLength={200}
                        {...form.getInputProps('name')}
                    />
                    <TextInput
                        label={t('platform.auth.signUp.organization')}
                        autoComplete="organization"
                        required
                        maxLength={200}
                        {...form.getInputProps('organization')}
                    />
                    <TextInput
                        label={t('platform.auth.email')}
                        type="email"
                        autoComplete="email"
                        required
                        maxLength={254}
                        {...form.getInputProps('email')}
                    />
                    <PasswordInput
                        label={t('platform.auth.password')}
                        autoComplete="new-password"
                        required
                        minLength={12}
                        maxLength={128}
                        {...form.getInputProps('password')}
                    />
                    <PasswordInput
                        label={t('platform.auth.resetPassword.confirmation')}
                        autoComplete="new-password"
                        required
                        maxLength={128}
                        {...form.getInputProps('confirmation')}
                    />
                    <Checkbox
                        checked={accepted}
                        onChange={(event) => setAccepted(event.target.checked)}
                        required
                        label={
                            <>
                                {t('platform.auth.signUp.accept')}{' '}
                                <a className={linkStyles} href={config.data.termsUrl} target="_blank" rel="noreferrer">
                                    {t('platform.auth.signUp.terms')}
                                </a>
                            </>
                        }
                    />
                    <Suspense fallback={<Spinner label={t('platform.shell.loading')} />}>
                        <RegistrationProtection key={proofKey} onProof={setProof} />
                    </Suspense>
                    <Button type="submit" loading={pending} disabled={!accepted || !proof}>
                        {t('platform.auth.signUp.submit')}
                    </Button>
                </form>
            )}
            <Link to="/sign-in" className={`text-sm ${linkStyles}`}>
                {t('platform.auth.backToSignIn')}
            </Link>
        </AuthScreen>
    );
}
